import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Typography,
  Paper,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { romaneioService } from '../../services/romaneio';
import DatePickerField from '../common/DatePickerField';
import { format, parse } from 'date-fns';

const RomaneioAlterarDatas: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    idFuncionario: '',
    funcionario: '',
    praca: '',
    idPraca: '',
    dataSaidaAtual: '',
    novaDataSaida: '',
    dataRetornoAtual: '',
    novaDataRetorno: '',
    finalizado: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Buscar dados do romaneio
  const { data: romaneioData, isLoading } = useQuery({
    queryKey: ['romaneio', id],
    queryFn: () => romaneioService.getById(id!),
    enabled: !!id,
  });

  // Carregar dados quando o romaneio for carregado
  React.useEffect(() => {
    if (romaneioData) {
      setFormData({
        idFuncionario: romaneioData.idFuncionario || '',
        funcionario: romaneioData.funcionario || '',
        praca: romaneioData.praca || '',
        idPraca: romaneioData.idPraca || '',
        dataSaidaAtual: romaneioData.dataSaidaString || '',
        novaDataSaida: romaneioData.dataSaidaString || '',
        dataRetornoAtual: romaneioData.dataRetornoString || '',
        novaDataRetorno: romaneioData.dataRetornoString || '',
        finalizado: romaneioData.finalizado || false,
      });
    }
  }, [romaneioData]);

  // Função para converter data do formato brasileiro para ISO
  const convertToApiFormat = (dateString: string): string => {
    if (!dateString) return '';
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  };

  // Função para converter data do formato ISO para brasileiro
  const convertToDisplayFormat = (dateString: string): string => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const handleBack = () => {
    navigate('/romaneio');
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.novaDataSaida) {
      newErrors.novaDataSaida = 'Nova data de saída é obrigatória';
    }

    if (!formData.novaDataRetorno) {
      newErrors.novaDataRetorno = 'Nova data de retorno é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Mutation para salvar datas
  const saveMutation = useMutation({
    mutationFn: (data: typeof formData) => {
      const dataSaidaAtualDate = new Date(convertToApiFormat(data.dataSaidaAtual));
      const dataRetornoAtualDate = new Date(convertToApiFormat(data.dataRetornoAtual));

      return romaneioService.salvarDatas({
        id: id!,
        novaDataSaidaRomaneio: convertToApiFormat(data.novaDataSaida),
        dataSaidaRomaneio: dataSaidaAtualDate.toISOString(),
        novaDataRetornoRomaneio: convertToApiFormat(data.novaDataRetorno),
        dataRetornoRomaneio: dataRetornoAtualDate.toISOString(),
        finalizado: data.finalizado,
      });
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['romaneios'] });
      setSnackbar({ open: true, message: 'Datas atualizadas com sucesso!' });
      setTimeout(() => {
        navigate('/romaneio');
      }, 1000);
    },
    onError: (error: any) => {
      console.error('Erro ao salvar datas:', error);
      setSnackbar({ open: true, message: 'Erro ao atualizar datas. Tente novamente.' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    saveMutation.mutate(formData);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Paper sx={{ p: 3 }}>
        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              Voltar
            </Button>
            <Typography variant="h5" component="h1">
              Alterar Datas do Romaneio #{id}
            </Typography>
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          {/* Funcionário e Praça (readonly) */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <TextField
              label="Funcionário"
              value={formData.funcionario}
              slotProps={{
                input: { readOnly: true }
              }}
              fullWidth
            />
            <TextField
              label="Praça"
              value={formData.praca}
              slotProps={{
                input: { readOnly: true }
              }}
              fullWidth
            />
          </Box>

          {/* Datas */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <TextField
              label="Data de Saída Atual"
              value={formData.dataSaidaAtual}
              slotProps={{
                input: { readOnly: true }
              }}
              fullWidth
            />
            <DatePickerField
              label="Nova Data de Saída"
              value={formData.novaDataSaida}
              onChange={(value) => setFormData(prev => ({ ...prev, novaDataSaida: value }))}
              error={!!errors.novaDataSaida}
              helperText={errors.novaDataSaida}
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
            <TextField
              label="Data de Retorno Atual"
              value={formData.dataRetornoAtual}
              slotProps={{
                input: { readOnly: true }
              }}
              fullWidth
            />
            <DatePickerField
              label="Nova Data de Retorno"
              value={formData.novaDataRetorno}
              onChange={(value) => setFormData(prev => ({ ...prev, novaDataRetorno: value }))}
              error={!!errors.novaDataRetorno}
              helperText={errors.novaDataRetorno}
            />
          </Box>

          {/* Status */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.finalizado}
                  onChange={(e) => setFormData(prev => ({ ...prev, finalizado: e.target.checked }))}
                />
              }
              label={formData.finalizado ? 'Status: FINALIZADO' : 'Status: INICIADO'}
            />
          </Box>

          {/* Botões */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={saveMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              type="submit"
              startIcon={<SaveIcon />}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </Box>
        </form>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.message.includes('sucesso') ? 'success' : 'error'}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RomaneioAlterarDatas;
